import { useMutation } from '@apollo/client/react'
import { Formik } from 'formik'
import React from 'react'

import { Button } from '../../../../../components/Button'
import { Currency } from '../../../../../components/Currency'
import { DisplayDate } from '../../../../../components/DisplayDate'
import { ButtonColumn, ButtonRow, Heading1, Row, Text } from '../../../../../components/layout'
import { CheckboxInput } from '../../../../../components/layout/form/input'
import { ScrollToError } from '../../../../../components/layout/form/ScrollToError'
import { FormikForm as Form } from '../../../../../components/layout/form/style'
import { Loading } from '../../../../../components/responses'
import {
  setErrorNotifications,
  setFatalErrorNotification,
} from '../../../../../components/responses/setErrorNotifications'
import { TopNavigation } from '../../../../../components/TopNavigation/TopNavigation'
import { CONFIRM_BILLING } from './confirmMutation'
import { messages as billingConfirmationMessages, billingConfirmationMessages as confirmationMessages } from './messages'
import { toConfirmBillingMutationVariables } from './toConfirmBillingMutationVariables'
import { Billing, ConfirmBillingFormValues } from './types'
import { formatMessageSafe } from '../../../../../util/translateInputOptions'
import { useIntl } from 'react-intl'

interface ModalProps {
  billing: Billing
  towingRecordId: number
  onConfirmBilling: () => void
  close: () => void
  continueEditing: () => void
}

const BillingConfirmationIntl: React.FunctionComponent<ModalProps> = ({
  billing,
  onConfirmBilling,
  towingRecordId,
  close,
  continueEditing,
}) => {
  const intl = useIntl()
  const {
    type,
    paymentDetails: { vattedAmount, debtorName, receiptEmail, paymentDate },
  } = billing
  const formatMessage = formatMessageSafe(intl)
  const [confirmBilling, { loading }] = useMutation(CONFIRM_BILLING, {
    onCompleted(result: any) {
      if (result.confirmBilling.__typename === 'ConfirmBillingSuccess') {
        onConfirmBilling()
      } else {
        setErrorNotifications({ data: result.confirmBilling })
      }
    },
    onError(err) {
      setFatalErrorNotification(err)
    },
  })

  return (
    <Formik
      initialValues={{ towingRecordId, sendReceipt: false }}
      onSubmit={(values: ConfirmBillingFormValues) =>
        confirmBilling({
          variables: toConfirmBillingMutationVariables(values),
        })
      }
    >
      {() => {
        return (
          <Form onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && e.preventDefault()}>
            <TopNavigation />
            <Row justify="center">
              <Heading1>{formatMessage(confirmationMessages.heading)}</Heading1>
            </Row>
            <Row justify="center">
              <Text fontWeight="700" size="big">
                {formatMessage(billingConfirmationMessages[type])} <Currency>{vattedAmount ?? 0}</Currency>
              </Text>
            </Row>
            <Row justify="center">
              <Text size="big">{debtorName}</Text>
            </Row>
            <Row justify="center">
              <Text size="big">
                <DisplayDate date={paymentDate} />
              </Text>
            </Row>
            {receiptEmail != null && receiptEmail !== '' && (
              <Row justify="center">
                <CheckboxInput
                  name="sendReceipt"
                  label={formatMessage(confirmationMessages.sendReceiptLabel, { receiptEmail })}
                />
              </Row>
            )}

            <Row justify="center">
              <ButtonRow>
                <ButtonColumn>
                  <Button category="save" label={formatMessage(confirmationMessages.markAsPaidButtonLabel)} submit />
                </ButtonColumn>
                <ButtonColumn>
                  <Button category="new" label={formatMessage(confirmationMessages.leaveOpenButtonLabel)} onClick={close} />
                </ButtonColumn>
                <ButtonColumn>
                  <Button category="new" label={formatMessage(confirmationMessages.continueEditingButtonLabel)} onClick={continueEditing} />
                </ButtonColumn>
              </ButtonRow>
            </Row>
            <ScrollToError />
            <Loading loading={loading} />
          </Form>
        )
      }}
    </Formik>
  )
}

export const BillingConfirmation = BillingConfirmationIntl
